import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Search, Pencil, Trash2, Loader2, UserPlus, ChevronRight, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getScenarioLabel, SCENARIO_LABEL } from '@/utils/labels';
import { StudentIntakeLinkModal } from '@/components/StudentIntakeLinkModal';
import { bordeRasgado, RUIDO_PAPEL } from '@/components/Collage';
import {
  TITULO, ROTULO, BTN_CLARO, colorEscenario, NOMBRE_PRUEBA,
} from '@/lib/estiloApp';

// Panel unificado (01/11/2026): una sola lista de personas con todo lo que
// antes estaba repartido entre Alumnos y Entrevistas. Arriba, la bandeja
// "Para hacer"; abajo, los filtros juntos y una fila por alumno.

// Filtros de estado. Primero lo que hay que accionar.
const FILTROS = [
  { key: '', label: 'Todos' },
  { key: 'vencido', label: 'Retest vencido' },
  { key: 'proximo', label: 'Retest próximo' },
  { key: 'apto', label: 'Apto pendiente' },
  { key: 'sin_mediciones', label: 'Sin entrevista' },
  { key: 'al_dia', label: 'Al día' },
];

const cumpleFiltro = (a, filtro) => {
  if (!filtro) return true;
  if (filtro === 'apto') return a.apto_pendiente;
  return a.estado_retest === filtro;
};

// Escenarios para el filtro, sin los alias viejos.
const ESCENARIOS = [
  'PUEDE_INICIAR', 'APTO_PARA_EJERCICIO_MODERADO', 'APTO_PARA_PROGRESAR_CON_CONTROL',
  'APTO_REQUERIDO', 'DERIVACION_INMEDIATA',
];
const ALIAS_ESCENARIO = {
  APTO_RECOMENDABLE: 'APTO_PARA_EJERCICIO_MODERADO',
  APTO_PARA_PROGRESAR: 'APTO_PARA_PROGRESAR_CON_CONTROL',
};

const ESTADO_RETEST = {
  vencido: {
    chip: 'bg-red-50 text-red-700 border-red-200',
    texto: (a) => `Vencido hace ${Math.abs(a.dias_para_retest)} día${Math.abs(a.dias_para_retest) === 1 ? '' : 's'}`,
  },
  proximo: {
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    texto: (a) =>
      a.dias_para_retest === 0
        ? 'Retest hoy'
        : `Retest en ${a.dias_para_retest} día${a.dias_para_retest === 1 ? '' : 's'}`,
  },
  al_dia: {
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    texto: (a) => `Al día · ${formatFecha(a.proximo_retest)}`,
  },
  sin_mediciones: {
    chip: 'bg-slate-50 text-slate-500 border-slate-200',
    texto: () => 'Sin entrevista',
  },
};

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function haceCuanto(iso) {
  if (!iso) return '';
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return `hace ${dias} días`;
}

const ALUMNO_VACIO = {
  nombre: '',
  sexo: '',
  fecha_nacimiento: '',
  telefono: '',
  email: '',
  intervalo_retest_meses: 3,
};

/** Hoja de papel rasgado, la de la landing pero más quieta. */
function Papel({ children }) {
  return (
    <div style={{ filter: 'drop-shadow(0 3px 0 rgba(0,0,0,0.35))', transform: 'rotate(-0.25deg)' }}>
      <div
        className="text-[#14110d] px-5 sm:px-6 py-5"
        style={{ backgroundColor: '#e2d9c3', backgroundImage: RUIDO_PAPEL, clipPath: bordeRasgado(3, 1.1, 26) }}
      >
        {children}
      </div>
    </div>
  );
}

/** Una columna de la bandeja "Para hacer". */
function ColumnaBandeja({ titulo, items, vacio, onMas }) {
  const primeros = items.slice(0, 3);
  return (
    <div className="min-w-0">
      <p className={`${ROTULO} text-[12px] flex items-baseline gap-2 mb-2`}>
        <span className={`${TITULO} text-[22px] tracking-normal`}>{items.length}</span>
        {titulo}
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-[#5a5245]">{vacio}</p>
      ) : (
        <div className="space-y-1.5">
          {primeros.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={it.onClick}
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#14110d]/20 bg-white/60 hover:bg-white/90 px-3 py-1.5 text-left text-sm transition-colors"
            >
              <span className="truncate font-medium">{it.nombre}</span>
              <span className="text-xs text-[#5a5245] whitespace-nowrap">{it.detalle}</span>
            </button>
          ))}
          {items.length > primeros.length && (
            <button type="button" onClick={onMas} className="text-xs text-[#5a5245] underline pl-1">
              y {items.length - primeros.length} más
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AlumnosPage() {
  const navigate = useNavigate();
  const [alumnos, setAlumnos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [escenario, setEscenario] = useState('');
  const [grupo, setGrupo] = useState('');
  const [linkAbierto, setLinkAbierto] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null); // null = alta, objeto = edición
  const [form, setForm] = useState(ALUMNO_VACIO);
  const [guardando, setGuardando] = useState(false);

  const [aEliminar, setAEliminar] = useState(null);
  const [resumen, setResumen] = useState(null);

  const cargarResumen = useCallback(() => {
    api.getResumenAlumnos()
      .then(setResumen)
      .catch(() => { /* la bandeja es informativa: si falla, no molesta */ });
  }, []);

  // Se trae la lista completa una vez y se filtra acá: así los filtros se
  // combinan y responden al instante.
  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getAlumnos({});
      setAlumnos(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Error al cargar alumnos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    cargarResumen();
    api.getGroups().then((g) => setGrupos(g || [])).catch(() => setGrupos([]));
  }, [cargar, cargarResumen]);

  const nombreGrupo = useMemo(() => {
    const m = new Map();
    grupos.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [grupos]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return alumnos.filter((a) => {
      if (q && !a.nombre.toLowerCase().includes(q)) return false;
      if (!cumpleFiltro(a, filtro)) return false;
      const est = ALIAS_ESCENARIO[a.ultimo_estado] || a.ultimo_estado;
      if (escenario === 'sin' && est) return false;
      if (escenario && escenario !== 'sin' && est !== escenario) return false;
      if (grupo === 'none' && a.grupo_id) return false;
      if (grupo && grupo !== 'none' && a.grupo_id !== grupo) return false;
      return true;
    });
  }, [alumnos, busqueda, filtro, escenario, grupo]);

  const hayFiltros = !!(busqueda || filtro || escenario || grupo);

  // Bandeja "Para hacer".
  const porRevisar = (resumen?.por_revisar || []).map((p) => ({
    key: p.id, nombre: p.nombre || 'Sin nombre', detalle: haceCuanto(p.created_at),
    onClick: () => navigate(`/evaluation/${p.id}`),
  }));
  const aptos = alumnos.filter((a) => a.apto_pendiente).map((a) => ({
    key: a.id, nombre: a.nombre, detalle: `desde ${formatFecha(a.ultima_medicion_fecha).slice(0, 5)}`,
    onClick: () => navigate(`/alumnos/${a.id}`),
  }));
  const vencidos = alumnos
    .filter((a) => a.estado_retest === 'vencido')
    .sort((x, y) => x.dias_para_retest - y.dias_para_retest)
    .map((a) => ({
      key: a.id, nombre: a.nombre, detalle: haceCuanto(a.proximo_retest),
      onClick: () => navigate(`/alumnos/${a.id}`),
    }));

  const abrirAlta = () => {
    setEditando(null);
    setForm(ALUMNO_VACIO);
    setFormOpen(true);
  };

  const abrirEdicion = (a) => {
    setEditando(a);
    setForm({
      nombre: a.nombre || '',
      sexo: a.sexo || '',
      fecha_nacimiento: a.fecha_nacimiento || '',
      telefono: a.telefono || '',
      email: a.email || '',
      intervalo_retest_meses: a.intervalo_retest_meses ?? 3,
    });
    setFormOpen(true);
  };

  const guardar = async () => {
    const nombre = form.nombre.trim();
    if (!nombre) {
      toast.error('Ingresá el nombre del alumno');
      return;
    }
    const payload = {
      ...form,
      nombre,
      intervalo_retest_meses: Number(form.intervalo_retest_meses) || 3,
    };
    // El backend espera null, no cadena vacía, en los campos opcionales.
    ['sexo', 'fecha_nacimiento', 'telefono', 'email'].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });

    try {
      setGuardando(true);
      if (editando) {
        await api.updateAlumno(editando.id, payload);
        toast.success('Alumno actualizado');
      } else {
        await api.createAlumno(payload);
        toast.success('Alumno creado');
      }
      setFormOpen(false);
      cargar();
      cargarResumen();
    } catch (e) {
      toast.error(e?.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    if (!aEliminar) return;
    try {
      await api.deleteAlumno(aEliminar.id);
      toast.success('Alumno eliminado');
      setAEliminar(null);
      cargar();
      cargarResumen();
    } catch (e) {
      // El backend devuelve 409 con la explicación cuando tiene entrevistas.
      toast.error(e?.message || 'No se pudo eliminar');
      setAEliminar(null);
    }
  };

  const select = 'h-9 rounded-full border border-slate-300 bg-white px-3 text-sm text-slate-700';
  const columnas = 'md:grid-cols-[minmax(170px,1.5fr)_1.5fr_1.1fr_1fr_0.9fr_88px]';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Encabezado */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className={`${ROTULO} text-[11px] text-slate-500`}>Tu panel</p>
            <h1 className={`${TITULO} text-[40px] leading-none text-slate-900 mt-1`}>Alumnos</h1>
            <p className="text-sm text-slate-500 mt-2">
              {alumnos.length} {alumnos.length === 1 ? 'activo' : 'activos'}
              {resumen?.entrevistas_del_mes != null &&
                ` · ${resumen.entrevistas_del_mes} ${resumen.entrevistas_del_mes === 1 ? 'entrevista' : 'entrevistas'} este mes`}
            </p>
          </div>
          <button type="button" onClick={() => setLinkAbierto(true)} className={BTN_CLARO}
            data-testid="generate-intake-link-btn">
            <LinkIcon className="h-4 w-4" />
            Mandar link de entrevista
          </button>
        </div>

        {/* Para hacer */}
        <Papel>
          <h2 className={`${TITULO} text-[22px] mb-3`}>Para hacer</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ColumnaBandeja
              titulo="Llegaron por link, sin revisar"
              items={porRevisar}
              vacio="Nada nuevo."
              onMas={() => navigate('/evaluations')}
            />
            <ColumnaBandeja
              titulo="Apto médico pendiente"
              items={aptos}
              vacio="Ninguno pendiente."
              onMas={() => setFiltro('apto')}
            />
            <ColumnaBandeja
              titulo="Retest vencido"
              items={vencidos}
              vacio="Todos al día."
              onMas={() => setFiltro('vencido')}
            />
          </div>
        </Papel>

        {/* Buscador y filtros */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre..."
              className="pl-10 rounded-full bg-white"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FILTROS.map((f) => (
              <button
                key={f.key || 'todos'}
                type="button"
                onClick={() => setFiltro(f.key)}
                className={`h-9 px-3.5 rounded-full text-sm border transition ${
                  filtro === f.key
                    ? 'bg-[#0b0b0c] text-[#efe6d2] border-[#0b0b0c]'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
                }`}
              >
                {f.label}
              </button>
            ))}
            <select value={escenario} onChange={(e) => setEscenario(e.target.value)} className={select}
              aria-label="Filtrar por escenario">
              <option value="">Escenario: todos</option>
              {ESCENARIOS.map((k) => <option key={k} value={k}>{SCENARIO_LABEL[k]}</option>)}
              <option value="sin">Sin entrevista</option>
            </select>
            <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className={select}
              aria-label="Filtrar por grupo">
              <option value="">Grupo: todos</option>
              <option value="none">Sin grupo</option>
              {grupos.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            {hayFiltros && (
              <button
                type="button"
                onClick={() => { setBusqueda(''); setFiltro(''); setEscenario(''); setGrupo(''); }}
                className="text-sm text-slate-500 underline px-1"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Listado */}
        {loading ? (
          <Card className="border-gray-200 bg-white">
            <CardContent className="py-16 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </CardContent>
          </Card>
        ) : visibles.length === 0 ? (
          <Card className="border-gray-200 bg-white">
            <CardContent className="py-16 text-center space-y-3">
              <UserPlus className="h-10 w-10 text-gray-300 mx-auto" />
              <p className="text-gray-500">
                {hayFiltros
                  ? 'No hay alumnos que coincidan con la búsqueda.'
                  : 'Todavía no cargaste ningún alumno.'}
              </p>
              {!hayFiltros && (
                <Button onClick={() => navigate('/new-evaluation')} variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Entrevistar al primero
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2.5">
            <div className={`hidden md:grid ${columnas} gap-4 pl-6 pr-4 ${ROTULO} text-[10.5px] text-slate-500`}>
              <span>Alumno</span><span>Último escenario</span><span>Retest</span><span>Pruebas</span><span>Grupo</span><span />
            </div>
            {visibles.map((a) => {
              const est = ESTADO_RETEST[a.estado_retest] || ESTADO_RETEST.sin_mediciones;
              const color = colorEscenario(a.ultimo_estado);
              const tipos = (a.ultima_prueba_tipos || []).map((t) => NOMBRE_PRUEBA[t] || t).join(', ');
              const nombreDelGrupo = a.grupo_id ? nombreGrupo.get(a.grupo_id) : null;
              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/alumnos/${a.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/alumnos/${a.id}`); }}
                  className={`group grid grid-cols-[1fr_auto] ${columnas} items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 border-l-[6px] bg-white pl-4 pr-3 py-3 cursor-pointer transition hover:border-slate-300 hover:shadow-[0_2px_0_rgba(0,0,0,0.08)]`}
                  style={{ borderLeftColor: color }}
                  data-testid={`alumno-fila-${a.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-['Manrope'] font-bold text-[15.5px] text-slate-900 truncate">{a.nombre}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {a.mediciones_count} {a.mediciones_count === 1 ? 'entrevista' : 'entrevistas'}
                      {a.sexo ? ` · ${a.sexo}` : ''}
                    </p>
                  </div>

                  {/* En el celular las acciones quedan arriba a la derecha;
                      en la compu, al final de la fila. */}
                  <div className="flex items-center justify-end gap-0.5 md:order-last">
                    <button type="button" title="Editar datos"
                      onClick={(e) => { e.stopPropagation(); abrirEdicion(a); }}
                      className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button type="button" title="Eliminar"
                      onClick={(e) => { e.stopPropagation(); setAEliminar(a); }}
                      className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <ChevronRight className="hidden md:block h-5 w-5 text-slate-300 group-hover:text-slate-500" />
                  </div>

                  <div className="col-span-2 md:col-span-1 flex flex-wrap items-center gap-2 text-sm">
                    {a.ultimo_estado ? (
                      <span className="inline-flex items-center gap-2 text-slate-800">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        {getScenarioLabel(a.ultimo_estado)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {a.apto_pendiente && (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-orange-50 text-orange-800 border-orange-200">
                        Apto pendiente
                      </span>
                    )}
                  </div>

                  <div className="col-span-2 md:col-span-1 flex flex-wrap items-center gap-2 md:block">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${est.chip}`}>
                      {est.texto(a)}
                    </span>
                    <span className="md:hidden text-xs text-slate-500">
                      {a.ultima_prueba_fecha ? `Pruebas: ${formatFecha(a.ultima_prueba_fecha)}` : ''}
                      {nombreDelGrupo ? ` · ${nombreDelGrupo}` : ''}
                    </span>
                  </div>

                  <div className="hidden md:block text-xs text-slate-500 min-w-0">
                    {a.ultima_prueba_fecha ? (
                      <>
                        <span className="block truncate text-slate-700">{tipos}</span>
                        {formatFecha(a.ultima_prueba_fecha)}
                      </>
                    ) : 'Sin pruebas'}
                  </div>

                  <div className="hidden md:block text-xs text-slate-500 truncate">
                    {nombreDelGrupo || '—'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Lo que todavía vive en la lista de entrevistas */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 text-sm text-slate-500">
          <button type="button" onClick={abrirAlta} className="inline-flex items-center gap-1.5 hover:text-slate-800">
            <UserPlus className="h-4 w-4" />
            Agregar un alumno sin entrevistarlo
          </button>
          <Link to="/evaluations" className="hover:text-slate-800 underline">
            Ver todas las entrevistas (ahí se asignan los grupos, por ahora)
          </Link>
        </div>
      </div>

      <StudentIntakeLinkModal open={linkAbierto} onOpenChange={setLinkAbierto} />

      {/* Alta / edición */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar alumno' : 'Nuevo alumno'}</DialogTitle>
            <DialogDescription>
              Solo el nombre es obligatorio. El resto lo podés completar después.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="al-nombre">Nombre completo *</Label>
              <Input
                id="al-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Nombre y apellido"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="al-sexo">Sexo</Label>
                <select
                  id="al-sexo"
                  value={form.sexo}
                  onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Sin especificar</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="No binario">No binario</option>
                  <option value="Prefiero no responder">Prefiero no responder</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="al-nac">Fecha de nacimiento</Label>
                <Input
                  id="al-nac"
                  type="date"
                  value={form.fecha_nacimiento || ''}
                  onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="al-tel">Teléfono</Label>
              <Input
                id="al-tel"
                value={form.telefono || ''}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="Opcional"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="al-email">Email</Label>
              <Input
                id="al-email"
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Opcional"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="al-intervalo">Retest cada (meses)</Label>
              <Input
                id="al-intervalo"
                type="number"
                min={1}
                max={24}
                value={form.intervalo_retest_meses}
                onChange={(e) => setForm({ ...form, intervalo_retest_meses: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                A partir de la última entrevista. Por defecto, 3 meses.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={guardando}>
              Cancelar
            </Button>
            <Button onClick={guardar} disabled={guardando} className="gap-2">
              {guardando && <Loader2 className="h-4 w-4 animate-spin" />}
              {editando ? 'Guardar cambios' : 'Crear alumno'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado */}
      <AlertDialog open={!!aEliminar} onOpenChange={(o) => !o && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {aEliminar?.nombre}?</AlertDialogTitle>
            <AlertDialogDescription>
              Solo se pueden eliminar alumnos sin entrevistas registradas. Si ya tiene
              historial, el sistema no lo va a permitir: en ese caso conviene marcarlo como
              inactivo para sacarlo del panel sin perder los registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
